import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

export interface SearchResult {
  id: string;
  title: string;
  description: string;
  type: 'service' | 'product' | 'professional' | 'page';
  url: string;
  imageUrl?: string;
}

// Mock data para demonstração - em uma aplicação real, isso viria de uma API
const searchData: SearchResult[] = [
  // Serviços
  {
    id: '1',
    title: 'Mapa Numerológico Completo',
    description: 'Análise completa do seu perfil numerológico com interpretação detalhada',
    type: 'service',
    url: '/app/loja-de-servicos',
    imageUrl: '/placeholder.svg'
  },
  {
    id: '2',
    title: 'Leitura de Tarô',
    description: 'Orientação espiritual através das cartas de tarô',
    type: 'service',
    url: '/app/loja-de-servicos',
    imageUrl: '/placeholder.svg'
  },
  {
    id: '3',
    title: 'Mapa Astral',
    description: 'Análise completa do seu mapa astral',
    type: 'service',
    url: '/app/loja-de-servicos',
    imageUrl: '/placeholder.svg'
  },
  // Profissionais
  {
    id: '4',
    title: 'Mariana Oliveira',
    description: 'Numeróloga e Tarologista especializada',
    type: 'professional',
    url: '/app/loja-de-servicos',
    imageUrl: '/placeholder.svg'
  },
  {
    id: '5',
    title: 'Ricardo Santos',
    description: 'Astrólogo com mais de 10 anos de experiência',
    type: 'professional',
    url: '/app/loja-de-servicos',
    imageUrl: '/placeholder.svg'
  },
  // Páginas
  {
    id: '6',
    title: 'Meu Mapa Numerológico',
    description: 'Acesse seus mapas numerológicos salvos',
    type: 'page',
    url: '/app/mapa-numerologico'
  },
  {
    id: '7',
    title: 'Carrinho de Compras',
    description: 'Visualize e gerencie seus itens no carrinho',
    type: 'page',
    url: '/app/carrinho'
  },
  {
    id: '8',
    title: 'Meus Produtos',
    description: 'Gerencie seus produtos e serviços adquiridos',
    type: 'page',
    url: '/app/meus-produtos'
  },
  {
    id: '9',
    title: 'Perfil',
    description: 'Gerencie suas informações pessoais',
    type: 'page',
    url: '/app/perfil'
  },
  {
    id: '10',
    title: 'Cupons de Desconto',
    description: 'Crie e gerencie seus cupons de desconto',
    type: 'page',
    url: '/app/cupons'
  }
];

export function useSearch() {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const navigate = useNavigate();

  const results = useMemo(() => {
    if (!query.trim()) return [];
    
    const searchTerm = query.toLowerCase().trim();
    return searchData.filter(item => 
      item.title.toLowerCase().includes(searchTerm) ||
      item.description.toLowerCase().includes(searchTerm)
    ).slice(0, 8); // Limitar a 8 resultados
  }, [query]);

  const search = (searchQuery: string) => {
    setQuery(searchQuery);
    setIsSearching(true);
    
    // Simular delay da pesquisa
    setTimeout(() => {
      setIsSearching(false);
      if (searchQuery.trim()) {
        setShowResults(true);
      }
    }, 300);
  };

  const clearSearch = () => {
    setQuery('');
    setShowResults(false);
    setIsSearching(false);
  };

  const navigateToResult = (result: SearchResult) => {
    navigate(result.url);
    clearSearch();
  };

  const navigateToSearchPage = () => {
    if (query.trim()) {
      navigate(`/app/pesquisa?q=${encodeURIComponent(query.trim())}`);
      clearSearch();
    }
  };

  // Fechar resultados quando clicar fora
  useEffect(() => {
    const handleClickOutside = () => {
      setShowResults(false);
    };

    if (showResults) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showResults]);

  return {
    query,
    setQuery,
    results,
    isSearching,
    showResults,
    setShowResults,
    search,
    clearSearch,
    navigateToResult,
    navigateToSearchPage
  };
}